/**
 * Uk Designer — header behaviour.
 *
 * Deliberately small: every animation lives in CSS, so this only manages state
 * (classes, ARIA, focus) and the few things CSS can't express.
 *
 * - Sticky state comes from an IntersectionObserver sentinel rather than a
 *   scroll listener, so nothing runs on the scroll thread.
 * - The cart badge subscribes to the theme's own cart event, imported from the
 *   same module the theme uses, rather than a guessed event name.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLElement} root */
function initSticky(root) {
  const header = root.querySelector('[data-pnav-sticky]');
  if (!header || !('IntersectionObserver' in window)) return;

  // The sentinel has to sit *outside* the sticky wrapper — inside it, it would
  // travel with the header and never leave the viewport. The negative margin
  // keeps its 1px out of the layout.
  const wrapper = header.closest('.shopify-section') ?? header;
  if (!wrapper.parentElement) return;

  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;';
  wrapper.parentElement.insertBefore(sentinel, wrapper);

  new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-stuck', !entry.isIntersecting),
    { threshold: 0 }
  ).observe(sentinel);
}

/** @param {HTMLElement} root */
function initSearch(root) {
  const wrap = root.querySelector('[data-pnav-search]');
  const toggle = root.querySelector('[data-pnav-search-toggle]');
  if (!wrap || !toggle) return;

  const input = wrap.querySelector('.pnav__search-input');
  if (!input) return;

  const setOpen = (open) => {
    wrap.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    // Keep the collapsed field out of the tab order entirely.
    input.setAttribute('tabindex', open ? '0' : '-1');
    if (open) input.focus();
    else toggle.focus();
  };

  toggle.addEventListener('click', () => {
    setOpen(!wrap.classList.contains('is-open'));
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  });

  // Close when focus or a click leaves the search cluster, but never swallow
  // the submit — an empty query simply collapses the field.
  document.addEventListener('pointerdown', (e) => {
    if (!wrap.classList.contains('is-open')) return;
    if (wrap.contains(/** @type {Node} */ (e.target))) return;
    wrap.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    input.setAttribute('tabindex', '-1');
  });

  wrap.querySelector('form')?.addEventListener('submit', (e) => {
    if (!input.value.trim()) {
      e.preventDefault();
      setOpen(false);
    }
  });
}

/** @param {HTMLElement} root */
function initCart(root) {
  const cart = root.querySelector('[data-pnav-cart]');
  const badge = root.querySelector('[data-pnav-badge]');
  const srCount = root.querySelector('[data-pnav-cart-count]');

  // The cart control is a real link to /cart, so it works with no JS at all.
  // If the theme's drawer is on the page, take over and open that instead.
  if (cart) {
    cart.addEventListener('click', (e) => {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || typeof drawer.toggle !== 'function') return;
      e.preventDefault();
      drawer.toggle();
    });
  }

  if (!badge) return {};

  const render = (count) => {
    const next = String(count ?? 0);
    if (badge.textContent.trim() === next) return;

    badge.textContent = next;
    badge.classList.toggle('is-empty', !count);
    if (srCount) srCount.textContent = `${next} items in cart`;

    if (REDUCED.matches || !count) return;
    badge.classList.remove('is-bumped');
    // Force a reflow so the animation restarts on repeat adds.
    void badge.offsetWidth;
    badge.classList.add('is-bumped');
  };

  return { render };
}

/** @param {HTMLElement} root */
function initDrawer(root) {
  const drawer = root.querySelector('[data-pnav-drawer]');
  const burger = root.querySelector('[data-pnav-burger]');
  if (!drawer || !burger) return;

  const panel = drawer.querySelector('.pnav-drawer__panel');
  let lastFocused = null;

  const open = () => {
    lastFocused = document.activeElement;
    drawer.hidden = false;
    // Next frame, so the transition runs from the closed state.
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('pnav-locked');
    panel?.querySelector('a')?.focus();
  };

  const close = () => {
    drawer.classList.remove('is-open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('pnav-locked');

    const finish = () => {
      drawer.hidden = true;
    };
    if (REDUCED.matches) finish();
    else panel?.addEventListener('transitionend', finish, { once: true });

    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };

  burger.addEventListener('click', () => {
    drawer.classList.contains('is-open') ? close() : open();
  });

  drawer.querySelector('[data-pnav-drawer-close]')?.addEventListener('click', close);
  drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (!drawer.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    // Trap Tab inside the panel while it's open.
    if (e.key !== 'Tab' || !panel) return;
    const items = panel.querySelectorAll('a[href], button:not([disabled])');
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // Leaving mobile with the drawer open would strand the scroll lock.
  window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
    if (e.matches && drawer.classList.contains('is-open')) close();
  });
}

function init() {
  const root = document.querySelector('.pnav-root');
  if (!root || root.dataset.pnavReady === 'true') return;
  root.dataset.pnavReady = 'true';

  initSticky(root);
  initSearch(root);
  initDrawer(root);
  const cart = initCart(root);

  // Subscribe to the theme's own cart event. Imported dynamically so a failure
  // here can never take the rest of the header down with it.
  if (cart?.render) {
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          event.promise
            ?.then(({ cart: data, detail }) => {
              cart.render(data?.totalQuantity ?? detail?.itemCount ?? 0);
            })
            .catch(() => {});
        });
      })
      .catch(() => {
        /* Badge stays at its server-rendered value. */
      });
  }
}

init();
document.addEventListener('shopify:section:load', init);
